import { supabase } from '../services/supabase'
import { createLogger } from './logger'

const logger = createLogger('auth')

export const PENDING_ACCESS_CODE_ID_KEY = 'pending_access_code_id'

export function persistPendingAccessCodeId(codeId) {
  if (codeId) {
    localStorage.setItem(PENDING_ACCESS_CODE_ID_KEY, codeId)
  }
}

export function readPendingAccessCodeId() {
  const fromLocal = localStorage.getItem(PENDING_ACCESS_CODE_ID_KEY)
  if (fromLocal) return fromLocal

  // One-time migration: older builds stored this in sessionStorage (per-tab),
  // which broke email confirmation opened in another tab.
  const fromSession = sessionStorage.getItem(PENDING_ACCESS_CODE_ID_KEY)
  if (fromSession) {
    localStorage.setItem(PENDING_ACCESS_CODE_ID_KEY, fromSession)
    sessionStorage.removeItem(PENDING_ACCESS_CODE_ID_KEY)
    return fromSession
  }

  return null
}

export function clearPendingAccessCodeId() {
  localStorage.removeItem(PENDING_ACCESS_CODE_ID_KEY)
  sessionStorage.removeItem(PENDING_ACCESS_CODE_ID_KEY)
}

export async function validateAccessCode(code) {
  const { data, error } = await supabase.rpc('validate_access_code', {
    input_code: code.trim(),
  })

  if (error) {
    logger.warn('Access code validation request failed', { action: 'validate_access_code' }, error)
    return { valid: false, error: error.message }
  }

  return data ?? { valid: false, error: 'Invalid access code' }
}

export async function linkAccessCodeToUser(codeId) {
  const { data, error } = await supabase.rpc('link_access_code_to_user', {
    p_code_id: codeId,
  })

  if (error) {
    logger.warn('Failed to link access code to user', { action: 'link_access_code_to_user' }, error)
    return { success: false, error: error.message }
  }

  if (!data?.success) {
    return { success: false, error: data?.error || 'Failed to link access code' }
  }

  return { success: true, alreadyLinked: Boolean(data.already_linked) }
}

/** Link pending code after signup. Claims storage synchronously so duplicate SIGNED_IN handlers no-op. */
export async function finalizePendingAccessCode() {
  const codeId = readPendingAccessCodeId()
  if (!codeId) {
    return { linked: false }
  }

  clearPendingAccessCodeId()

  const result = await linkAccessCodeToUser(codeId)
  if (result.success) {
    logger.info('Access code linked after signup', { action: 'finalize_access_code' })
    return { linked: true, alreadyLinked: result.alreadyLinked }
  }

  persistPendingAccessCodeId(codeId)
  logger.warn('Access code finalize failed', {
    action: 'finalize_access_code',
    error: result.error,
  })
  return { linked: false, error: result.error }
}
